import { ChartColumnIncreasing, Table as TableIcon } from "lucide-react";
import { Tab, Tabs } from "@heroui/react";
import { useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import {
  useGetFarmerAnimals,
} from "../features/Animals/animalApiSlice.ts";
import PieChart from "../features/Charts/PieChart.tsx";
import Chart from "../features/Charts/Chart.tsx";
import { OveralChart } from "../features/Charts/OveralChart.tsx";
import { AuthStore } from "../store/useAuthStore.ts";
import { ErrorFallback } from "../lib/Utils.Components.tsx";
import { Main } from "@/pages/Main.tsx";
import FarmerAnimals from "@/features/Farmers/Farmer.Animals.tsx";
import FarmForm from "@/features/Form/FarmForm.tsx";

export type TabKey = "Animals" | "Analytics" | "Overview";

const Overview = () => {
  const { data, refetch } = useGetFarmerAnimals();
  const [selected, setSelected] = useState<TabKey>("Overview");

  const ownFarm = AuthStore((state) => state.userInfo?.ownFarm);
  const farmInfoId = AuthStore((state) => state.farmInfo?.id);
  // Show the modal only when NEITHER ownFarm (from JWT/store) NOR farmInfo.id
  // (set immediately on registration success) confirms a farm exists.
  // This dual-check prevents a stale JWT refresh from resetting ownFarm=false
  // and re-showing the modal after the user has just registered.
  const needsFarm = !ownFarm && !farmInfoId;

  const { userInfo } = AuthStore();
  const id = userInfo.id;

  const tableSectionRef = useRef<HTMLDivElement>(null);
  const analyticsSectionRef = useRef<HTMLDivElement>(null);

  const handleScrollToTable = () => {
    // First, ensure the correct tab is selected
    setSelected("Animals");

    // The tab needs a moment to render. A tiny timeout ensures the
    // element is in the DOM before we try to scroll to it.
    setTimeout(() => {
      tableSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100); // 100ms is usually enough
  };

  const handleScrollToAnalytics = () => {
    // First, ensure the correct tab is selected
    setSelected("Analytics");

    // The tab needs a moment to render. A tiny timeout ensures the
    // element is in the DOM before we try to scroll to it.
    setTimeout(() => {
      analyticsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100); // 100ms is usually enough
  };

  useEffect(() => {
    // A persisted query can be restored as successful before the login
    // navigation completes, which otherwise skips the initial network read.
    // Explicitly refresh whenever the authenticated farmer reaches Overview.
    if (id) void refetch();
  }, [id, refetch]);

  const maleAnimals =
    data && Array.isArray(data)
      ? data.filter((item: any) => item.sex === "male").length
      : 0;

  const femaleAnimals =
    data && Array.isArray(data)
      ? data.filter((item: any) => item.sex === "female").length
      : 0;

  return (
    <>
      {/* FarmForm is rendered outside the loading guard so it is NEVER
          unmounted by a refetch/isLoading cycle. The dual guard (ownFarm +
          farmInfo.id) prevents a stale JWT token refresh from re-showing it
          after the user has already registered. */}
      {needsFarm && <FarmForm />}

      <div
        className={
          "h-full w-full overflow-x-auto overflow-y-auto flex" +
          " flex-col" +
          " gap-4 px-2 relative left-10"
        }
      >
        <Main
            maleAnimals={maleAnimals}
            femaleAnimals={femaleAnimals}
            totalAnimals={data?.length ?? 0}
            setSelected={setSelected}
            onViewAnimalsClick={handleScrollToTable}
            onViewAnalyticsClick={handleScrollToAnalytics}
          />
      <Tabs
        key={"tabs"}
        classNames={{
          base: " z-50",
          tabList:
            " mx-1 border-gray-2 z-50 " +
            " overflow-x-auto dark:border-2" +
            " dark:border-stone-800",
          tabContent:
            "text-[#015354ff] z-50" + " group-data-[selected=true]:text-white ",
          cursor: "bg-[#008c8fff]",
        }}
        aria-label={"Tabs"}
        color={"default"}
        variant={"solid"}
        selectedKey={selected}
        onSelectionChange={(key) => setSelected(key as TabKey)}
      >
        <Tab key={"Overview"} title={"Overview"}></Tab>
        <Tab
          key={"Animals"}
          title={
            <div className="flex items-center space-x-2">
              <TableIcon size={"22px"} />
              <span>Animals</span>
            </div>
          }
        >
          {/* <div className="py-4 sm:py-2"> */}
          {/*   <div className="mx-auto max-w-7xl px-6 lg:px-8"> */}
          {/*     <div className="relative bottom-4 flex flex-row gap-4 lg:grid grid-cols-1 gap-x-4 gap-y-2 text-center lg:grid-cols-3"> */}
          {/*       <Card */}
          {/*         className={ */}
          {/*           "mx-auto h-28 lg:h-12 py-2  max-w-xs gap-2" + */}
          {/*           " gap-y-2" + */}
          {/*           " px-4" */}
          {/*         } */}
          {/*       > */}
          {/*         <div */}
          {/*           className={ */}
          {/*             "flex flex-col-reverse lg:flex-row-reverse gap-4" */}
          {/*           } */}
          {/*         > */}
          {/*           <dt className="text-base leading-7 text-gray-600 dark:text-gray-200"> */}
          {/*             Male Cattles */}
          {/*           </dt> */}
          {/*           <dd className="order-first text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-300 sm:text-2xl"> */}
          {/*             {maleAnimals} */}
          {/*           </dd> */}
          {/*         </div> */}
          {/*       </Card> */}
          {/*       <Card className="mx-auto h-28 lg:h-12 py-2 max-w-xs gap-2 px-4"> */}
          {/*         <div */}
          {/*           className={ */}
          {/*             "flex flex-col-reverse lg:flex-row-reverse gap-4" */}
          {/*           } */}
          {/*         > */}
          {/*           <dt className="text-base leading-7 text-gray-600 dark:text-gray-200"> */}
          {/*             Female Cattles */}
          {/*           </dt> */}
          {/*           <dd className="order-first text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-300 sm:text-2xl"> */}
          {/*             {femaleAnimals} */}
          {/*           </dd> */}
          {/*         </div> */}
          {/*       </Card> */}
          {/*       <Card className="mx-auto h-28 lg:h-12 py-2 px-4  max-w-xs items-center gap-2 center"> */}
          {/*         <div */}
          {/*           className={ */}
          {/*             "flex flex-col-reverse lg:flex-row-reverse gap-4" */}
          {/*           } */}
          {/*         > */}
          {/*           <dt */}
          {/*             className="text-base leading-7 */}
          {/* 					text-gray-600 dark:text-gray-200" */}
          {/*           > */}
          {/*             Total Animals */}
          {/*           </dt> */}
          {/*           <dd */}
          {/*             className="order-first text-xl */}
          {/* 					font-semibold tracking-tight text-gray-900 dark:text-gray-300 */}
          {/* 					sm:text-2xl" */}
          {/*           > */}
          {/*             {data && data?.length} */}
          {/*           </dd> */}
          {/*         </div> */}
          {/*       </Card> */}
          {/*     </div> */}
          {/*   </div> */}
          {/* </div> */}
          {/*<Suspense fallback={<div*/}
          {/*		className={'w-full h-full flex flex-col items-center justify-center'} >*/}
          {/*	<DNA*/}
          {/*			height="80"*/}
          {/*			width="80"*/}
          {/*			ariaLabel="bars-loading"*/}
          {/*			wrapperStyle={{}}*/}
          {/*			wrapperClass=""*/}
          {/*			visible={true}*/}
          {/*	/>*/}
          {/*	<span >Loading...</span >*/}
          {/*</div >} >*/}
          {/*	<LazyTable />*/}
          {/*</Suspense >*/}
          {/*<TableComp />*/}
          <div ref={tableSectionRef}>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              {/* <Table /> */}
              <FarmerAnimals />
            </ErrorBoundary>
          </div>
        </Tab>
        <Tab
          key={"Analytics"}
          title={
            <div className="flex items-center space-x-2">
              <ChartColumnIncreasing size={"22px"} />
              <span>Analytics</span>
            </div>
          }
        >
          <div className={"h-full"} ref={analyticsSectionRef}>
            <Tabs
              variant={"underlined"}
              classNames={{
                base: " z-50",
                tabList:
                  " mx-1 border-gray-2 z-50 " +
                  " overflow-x-auto dark:border-2" +
                  " dark:border-stone-800",
                tabContent:
                  "text-[#015354ff] z-50" +
                  "group-data-[selected=true]:text-green-600" +
                  " group-data-[selected=true]:font-bold",
                cursor: "bg-[#008c8fff] ",
              }}
            >
              <Tab key={"AnimalsPerBreed"} title={"Animals Per Breed"}>
                <PieChart />
              </Tab>
              <Tab
                key={"Animal Gender"}
                className={"h-full flex flex-col gap-10"}
                title={"Animals Gender"}
              >
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <Chart />
                </ErrorBoundary>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <OveralChart />
                </ErrorBoundary>
              </Tab>
            </Tabs>
          </div>
        </Tab>
      </Tabs>
      </div>
    </>
  );
};

export default Overview;
